/**
 * 护照（素材：UI-ASSET-005 v1）。
 * 正面是资料页，底图 passport-page-data.webp（照用户 2026-09-24 的参考样式画的空白横版资料页：玫瑰粉防伪纸、空照片框、
 *   空字段引导线、空机读码区，右上角自带徽记）。字全是页面按真实数据叠上去的，排在底图的格子里（位置见 life.css）：
 *   左上“宠物灵魂护照 / PETSOUL PASSPORT”与“纪念用 · 非真实证件”；中间“PETSOUL REPUBLIC”，下面两条细线上是类型 / 签发国 / 护照号码；
 *   照片框里是证件照，右上角压一枚签发章；右边两栏中英双语字段——只显示服务端 fields 真实给的，没有的不编；
 *   “主人签名”在右栏最下面那条签名线上（服务端没给签名就空着，不写假名字）；最下方两行机读码风格等宽字（只由真实数据拼出 PetSoul 自己的代号）。
 *   PETSOUL REPUBLIC / PSR / PETS 都是虚构的，不模仿任何真实国家。
 * 背面是可翻页的小本：封面之后是纪念章页（底图 passport-page-stamps.webp），章按服务端的真实 stamps（城市、盖章时间）盖：
 *   章形是 C 组 8 枚印章遮罩之一（按章的哈希挑），城市、日期两行字由页面写在章里（SVG textPath），轻微旋转，红 / 蓝 / 绿墨色按盖章先后轮换。
 */
import { useId, useState, type CSSProperties } from "react";
import type { CredentialDetail, CredentialSummary, PassportStamp } from "@/shared/contracts";
import { Button } from "@/shared/ui";
import { chunk, hashOf, passportMrz, stampDate } from "./copy";
import type { WalletPet } from "./data";
import { FictionBox, FieldGrid, IdPhoto } from "./parts";

export const STAMPS_PER_PAGE = 4;
const INKS = ["red", "blue", "green"] as const;

/**
 * UI-ASSET-005 v1 C 组印章遮罩（纯黑 + alpha，512×512，不带字）：纪念章与资料页照片上的签发章都用这一套；
 * 遮罩图与印泥颗粒（stamp-grain.webp）接在 life.css 的 .ps-inkstamp 上，章形外圈带颗粒。
 */
export const STAMP_SHAPES = ["circle", "double-circle", "rounded-rectangle", "mountain-triangle", "postmark-waves", "paw", "oval", "perforated-frame"] as const;
export type StampShape = (typeof STAMP_SHAPES)[number];

type StampLayout = {
  /** 城市（上）、日期（下）所在的路径，章面 120×120（和遮罩同一个正方形） */
  top: string;
  bottom: string;
  /** 字号 */
  city: number;
  date: number;
  /** 最长宽度：估算字宽超过就用 textLength 按比例压窄，不压到墨上 */
  cityMax: number;
  dateMax: number;
  /** 中间那枚小爪印的位置（没有就不画） */
  paw?: string;
};

/**
 * 每种章形里城市、日期两行字的位置。字都排在遮罩里没有墨的地方（按遮罩图实测的内圈、内框、图形下方；
 * 遮罩的缩放与位置见 life.css 的 .ps-inkstamp--*，两边一起改）：
 * - 两种圆章：沿内圈上弧写城市、下弧写日期，中间一枚小爪印（和原来的圆章一样）；
 * - 圆角方框、椭圆：框里两行横排；齿孔邮票框：城市在上、日期在下，中间一枚小爪印；
 * - 邮戳波浪：城市写在左边半圆里，日期写在整枚章下方；
 * - 山形三角、爪印：图形在上，城市、日期两行写在图形下方。
 */
export const STAMP_LAYOUTS: Record<StampShape, StampLayout> = {
  circle: { top: "M 29.5 60 A 30.5 30.5 0 0 1 90.5 60", bottom: "M 15.5 60 A 44.5 44.5 0 0 0 104.5 60", city: 15, date: 10, cityMax: 84, dateMax: 80, paw: "translate(46.5 46.5) scale(1.125)" },
  "double-circle": { top: "M 34.5 60 A 25.5 25.5 0 0 1 85.5 60", bottom: "M 22 60 A 38 38 0 0 0 98 60", city: 13, date: 9.5, cityMax: 70, dateMax: 70, paw: "translate(48.6 48.6) scale(0.95)" },
  "rounded-rectangle": { top: "M 16 62 L 104 62", bottom: "M 16 78 L 104 78", city: 16, date: 10.5, cityMax: 84, dateMax: 84 },
  "mountain-triangle": { top: "M 10 104 L 110 104", bottom: "M 20 116.5 L 100 116.5", city: 13, date: 9.5, cityMax: 96, dateMax: 76 },
  "postmark-waves": { top: "M 16 58 L 52 58", bottom: "M 20 117 L 100 117", city: 14, date: 9.5, cityMax: 36, dateMax: 76 },
  paw: { top: "M 10 104 L 110 104", bottom: "M 20 116.5 L 100 116.5", city: 13, date: 9.5, cityMax: 96, dateMax: 76 },
  oval: { top: "M 19 61.5 L 101 61.5", bottom: "M 23 75 L 97 75", city: 14, date: 9.5, cityMax: 80, dateMax: 72 },
  "perforated-frame": { top: "M 22 44 L 98 44", bottom: "M 22 94 L 98 94", city: 16, date: 10.5, cityMax: 72, dateMax: 72, paw: "translate(48 52)" },
};
const CITY_SPACING = 2;
const DATE_SPACING = 0.6;

/** 估算一行字的宽度（章面单位）：中日韩字按 1em、其余按 0.62em，日期按数字 0.58em，再加字距。 */
export function stampTextWidth(text: string, size: number, spacing: number, digits = false): number {
  const chars = [...text];
  const glyphs = chars.reduce((sum, ch) => sum + (digits ? 0.58 : (ch.codePointAt(0) ?? 0) > 0x2e80 ? 1 : 0.62) * size, 0);
  return glyphs + spacing * Math.max(0, chars.length - 1);
}

/** 城市名在某种章形里要压到的比例（1 = 不用压） */
export function cityFit(shape: StampShape, city: string): number {
  const layout = STAMP_LAYOUTS[shape];
  return Math.min(1, layout.cityMax / stampTextWidth(city, layout.city, CITY_SPACING));
}

/**
 * 章形：按章的哈希挑；城市名在这种章形里要压到 75% 以下（比如邮戳的左半圆只放得下两三个字）就顺着往后换一种放得下的，
 * 都放不下就用最宽的那种。只看这枚章自己的数据，同一枚章每次都是同一个样子。
 */
export function stampShapeFor(hash: number, city: string): StampShape {
  const start = (hash >>> 4) % STAMP_SHAPES.length;
  let best: StampShape = STAMP_SHAPES[start];
  for (let i = 0; i < STAMP_SHAPES.length; i += 1) {
    const shape = STAMP_SHAPES[(start + i) % STAMP_SHAPES.length];
    if (cityFit(shape, city) >= 0.75) return shape;
    if (cityFit(shape, city) > cityFit(best, city)) best = shape;
  }
  return best;
}

/**
 * 一枚章：章形是遮罩着墨色（外圈，带印泥颗粒；图没加载出来时外圈不显示），城市、日期两行字（SVG textPath）是纯墨色，
 * 写在章形里没有墨的地方，个别章形中间有一枚小爪印。字不依赖任何图片（颗粒图、遮罩图加载失败时字照样清楚）。
 * 纪念章与资料页照片上的签发章是同一套。字是真实数据，不烘焙进图。
 */
export function InkStamp({ top, bottom, seed, shape, label, testIds }: { top: string; bottom: string; seed: number; shape: StampShape; label?: string; testIds?: { top: string; bottom: string } }) {
  const uid = `st${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const layout = STAMP_LAYOUTS[shape];
  const fit = (width: number, max: number) => (width > max ? { textLength: max, lengthAdjust: "spacingAndGlyphs" as const } : {});
  // 每枚章的颗粒错开一点（按章的哈希），不会枚枚一样
  const grainAt = `${seed % 128}px ${(seed >>> 7) % 128}px`;
  return (
    <span className={`ps-inkstamp ps-inkstamp--${shape}`} style={{ "--stamp-grain-at": grainAt } as CSSProperties}>
      <span className="ps-inkstamp__mark" aria-hidden="true" />
      <svg viewBox="0 0 120 120" role={label ? "img" : undefined} aria-label={label || undefined} aria-hidden={label ? undefined : true}>
        <defs>
          <path id={`${uid}-top`} d={layout.top} />
          <path id={`${uid}-bottom`} d={layout.bottom} />
        </defs>
        <g fill="currentColor">
          {layout.paw ? (
            <g transform={layout.paw}>
              <circle cx="6.3" cy="10" r="2.1" />
              <circle cx="9.7" cy="6" r="2.2" />
              <circle cx="14.3" cy="6" r="2.2" />
              <circle cx="17.7" cy="10" r="2.1" />
              <path d="M12 11.8c-2.9 0-5.6 3-5.6 5.4 0 1.7 1.3 2.8 2.9 2.8 1.1 0 1.8-.5 2.7-.5s1.6.5 2.7.5c1.6 0 2.9-1.1 2.9-2.8 0-2.4-2.7-5.4-5.6-5.4z" />
            </g>
          ) : null}
          <text className="ps-stamp__city" data-testid={testIds?.top} style={{ fontSize: layout.city, letterSpacing: CITY_SPACING }}>
            <textPath href={`#${uid}-top`} startOffset="50%" textAnchor="middle" {...fit(stampTextWidth(top, layout.city, CITY_SPACING), layout.cityMax)}>
              {top}
            </textPath>
          </text>
          <text className="ps-stamp__date" data-testid={testIds?.bottom} style={{ fontSize: layout.date, letterSpacing: DATE_SPACING }}>
            <textPath href={`#${uid}-bottom`} startOffset="50%" textAnchor="middle" {...fit(stampTextWidth(bottom, layout.date, DATE_SPACING, true), layout.dateMax)}>
              {bottom}
            </textPath>
          </text>
        </g>
      </svg>
    </span>
  );
}

export function PassportDataPage({ detail, pet }: { detail: CredentialDetail; pet: WalletPet }) {
  const { summary, fields } = detail;
  // 护照号按参考样式放进抬头条（标签仍用服务端原文）；其余字段照服务端顺序排进正文两栏。
  const numberField = summary.number ? (fields.find((field) => field.value.trim() === summary.number) ?? null) : null;
  const bodyFields = numberField ? fields.filter((field) => field !== numberField) : fields;
  const place = fields.find((field) => field.label.trim() === "签发地")?.value.trim() || "PETSOUL";
  const issued = summary.issued_at ? stampDate(summary.issued_at) : "";
  const mrz = passportMrz(summary, pet.species);
  return (
    <article className="ps-passport-data ps-cred ps-cred--passport" data-testid="cred-front">
      <FictionBox />
      <header className="ps-passport-data__head">
        <div className="ps-passport-data__title">
          <strong>宠物灵魂护照</strong>
          <small lang="en">PETSOUL PASSPORT</small>
        </div>
        <div className="ps-passport-data__republic" lang="en">
          PETSOUL REPUBLIC
        </div>
        <dl className="ps-passport-data__meta">
          <div>
            <dt>
              类型 <small lang="en">Type</small>
            </dt>
            <dd>PETS</dd>
          </div>
          <div>
            <dt>
              签发国 <small lang="en">Code</small>
            </dt>
            <dd>PSR</dd>
          </div>
          {summary.number ? (
            <div className="is-number" data-testid="passport-number">
              <dt>
                {numberField?.label ?? "护照号码"} <small lang="en">Passport No.</small>
              </dt>
              <dd className="ps-mono">{summary.number}</dd>
            </div>
          ) : null}
        </dl>
      </header>
      <div className="ps-passport-data__body">
        <div className="ps-passport-data__photo">
          <IdPhoto pet={pet} />
          {issued ? (
            <span className="ps-passport-data__seal ps-stamp--red" aria-hidden="true">
              <InkStamp top={place} bottom={issued} seed={hashOf(summary.number ?? place)} shape="circle" />
            </span>
          ) : null}
        </div>
        <FieldGrid fields={bodyFields} bilingual />
      </div>
      <div className="ps-passport-data__sign" data-testid="owner-signature">
        <span>
          主人签名 <small lang="en">Owner&apos;s signature</small>
        </span>
        <i aria-hidden="true" />
      </div>
      {mrz ? (
        <p className="ps-passport-data__mrz ps-mono" data-testid="passport-mrz" aria-hidden="true">
          <span>{mrz[0]}</span>
          <span>{mrz[1]}</span>
        </p>
      ) : null}
    </article>
  );
}

/** 封面：底图是 UI-ASSET-005 v1 正式护照封面（life.css 里的背景，图上自带徽记、不带字），这里只叠标题和提示两行真实文字。 */
function PassportCover({ summary, count }: { summary: CredentialSummary; count: number }) {
  return (
    <div className="ps-passport-cover">
      <span className="ps-passport-cover__title">{summary.title ?? summary.label}</span>
      <span className="ps-passport-cover__hint">{count ? `已盖 ${count} 枚纪念章，翻开看看` : "还没有纪念章，翻开看看"}</span>
    </div>
  );
}

/**
 * 一页四个章位（章左上角占页面宽高的百分比；章宽是页宽的 42%，章面正方形），再按章各自的哈希微调、轻微旋转。
 * 对准纪念章页底图四角的淡色章框（实测中心：左上圆框 25% / 24%、右上圆角方框 72.5% / 23.5%、左下菱形框 27% / 72.5%、
 * 右下椭圆框 71.5% / 75.5%）；中间的花边圆框和右边的竖框留空。上排的章不高过 8%，不压住“纪念章 1 / 1”那一行。
 */
export const STAMP_SLOTS = [
  { x: 4, y: 11 },
  { x: 51.5, y: 11 },
  { x: 6, y: 57.5 },
  { x: 50.5, y: 59.5 },
];

/** order 是这枚章在整本护照里按时间的序号：墨色按序号轮换（红 → 蓝 → 绿），新章只会加在后面，旧章的墨色不会变。 */
export function Stamp({ stamp, slot, order }: { stamp: PassportStamp; slot: number; order: number }) {
  const hash = hashOf(`${stamp.journey_id}|${stamp.city}|${stamp.stamped_at}`);
  const ink = INKS[order % INKS.length];
  const shape = stampShapeFor(hash, stamp.city);
  const rotate = ((hash >>> 8) % 25) - 12;
  const base = STAMP_SLOTS[slot % STAMP_SLOTS.length];
  const left = base.x + (((hash >>> 12) % 7) - 3);
  const top = base.y + (((hash >>> 16) % 7) - 3);
  const date = stampDate(stamp.stamped_at);
  const style = { left: `${left}%`, top: `${top}%`, "--stamp-rotate": `${rotate}deg` } as CSSProperties;
  return (
    <figure className={`ps-stamp ps-stamp--${ink}`} style={style} data-shape={shape} data-testid="passport-stamp">
      <InkStamp top={stamp.city} bottom={date} seed={hash} shape={shape} label={`${stamp.city} 纪念章，${date}`} testIds={{ top: "stamp-city", bottom: "stamp-date" }} />
      <figcaption>{stamp.title}</figcaption>
    </figure>
  );
}

function StampPage({ stamps, index, count }: { stamps: PassportStamp[]; index: number; count: number }) {
  return (
    <div className="ps-stamp-page">
      <header className="ps-stamp-page__head">
        <span>纪念章</span>
        <small>
          {index} / {count}
        </small>
      </header>
      {stamps.length ? (
        stamps.map((stamp, slot) => (
          <Stamp key={`${stamp.journey_id}-${stamp.city}-${stamp.stamped_at}`} stamp={stamp} slot={slot} order={(index - 1) * STAMPS_PER_PAGE + slot} />
        ))
      ) : (
        <p className="ps-stamp-page__empty">还没有纪念章。TA 出远门到了目的地，会在这里盖上一枚。</p>
      )}
    </div>
  );
}

/** 背面：可翻页的小本（封面 → 纪念章页）。纪念章按盖章时间从早到晚排。 */
export function PassportBooklet({ detail }: { detail: CredentialDetail }) {
  const sorted = [...detail.stamps].sort((a, b) => Date.parse(a.stamped_at) - Date.parse(b.stamped_at));
  const stampPages = sorted.length ? chunk(sorted, STAMPS_PER_PAGE) : [[]];
  const last = stampPages.length; // 第 0 页是封面
  const [page, setPage] = useState(0);
  const [turn, setTurn] = useState<"next" | "prev" | null>(null);
  const go = (step: 1 | -1) => {
    const next = Math.min(last, Math.max(0, page + step));
    if (next === page) return;
    setTurn(step > 0 ? "next" : "prev");
    setPage(next);
  };
  return (
    <div className="ps-passport-book ps-cred ps-cred--passport" data-testid="cred-back">
      <div key={page} className={`ps-passport-book__sheet${turn ? ` is-turn-${turn}` : ""}`}>
        {page === 0 ? <PassportCover summary={detail.summary} count={sorted.length} /> : <StampPage stamps={stampPages[page - 1]} index={page} count={stampPages.length} />}
      </div>
      <nav className="ps-passport-book__nav" aria-label="护照翻页">
        <Button size="sm" variant="ghost" icon="back" disabled={page === 0} onClick={() => go(-1)}>
          上一页
        </Button>
        <span aria-live="polite">{page === 0 ? "封面" : `纪念章 ${page} / ${stampPages.length}`}</span>
        <Button size="sm" variant="ghost" disabled={page === last} onClick={() => go(1)}>
          {page === 0 ? "翻开" : "下一页"}
        </Button>
      </nav>
    </div>
  );
}
